import { useState, type ReactNode } from "react";
import { FieldArray, Form, Formik } from "formik";
import { IconPlus, IconTrash } from "@tabler/icons-react";
import ModalDialog, {
  ModalDialogBody,
  ModalDialogFooter,
} from "../UI/ModalDialog";
import Button from "../UI/Button";
import FormikSubmitButton from "../UI/FormikSubmitButton";
import IconButton from "../UI/IconButton";
import Card from "../UI/Card";
import Status from "../UI/Status";
import Text from "../UI/Text";
import Callout from "../UI/Callout";
// @ts-expect-error pure js
import TextInput from "../UI/TextInput";
// @ts-expect-error pure js
import Checkbox from "../UI/Checkbox";
// @ts-expect-error pure js
import { currencyFormatter } from "../../api/Functions";
// @ts-expect-error pure js
import { sanitizeValues } from "../../models/utils";
// @ts-expect-error pure js
import { handleAPIError } from "../../utils";
// @ts-expect-error pure js
import { regenerateBalanceSchema } from "../../models/regenerateBalance";
import type { Tenant } from "../../models/tenant";
import styles from "./RegenerateBalanceForm.module.css";

interface PalierFormValues {
  date_palier: string;
  loyer_hc: number | string;
  charges: number | string;
}

interface RegenerateFormValues {
  paliers: PalierFormValues[];
  loyer_a_jour: boolean;
}

interface RegenerateBalanceFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tenant: Tenant;
  onSubmit: (values: Record<string, unknown>) => Promise<void>;
}

function palierTotal(palier: PalierFormValues): number {
  return (Number(palier.loyer_hc) || 0) + (Number(palier.charges) || 0);
}

export default function RegenerateBalanceForm({
  open,
  onOpenChange,
  tenant,
  onSubmit,
}: RegenerateBalanceFormProps) {
  const [error, setError] = useState<ReactNode>(null);

  const initialValues: RegenerateFormValues = {
    paliers: [
      {
        date_palier: tenant.debut,
        loyer_hc: tenant.loyer_hc ?? "",
        charges: tenant.charges ?? "",
      },
    ],
    loyer_a_jour: true,
  };

  async function submitHandler(
    values: RegenerateFormValues,
    { resetForm }: { resetForm: () => void },
  ) {
    setError(null);
    try {
      await onSubmit(sanitizeValues(values));
      resetForm();
      onOpenChange(false);
    } catch (err) {
      setError(handleAPIError(err));
    }
  }

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize={true}
      validationSchema={regenerateBalanceSchema}
      onSubmit={submitHandler}
    >
      {({ values, resetForm }) => (
        <ModalDialog
          size="md"
          open={open}
          header="Régénérer l'historique de paiement"
          subtitle="Reconstituez les appels de loyer, appels de charges et paiements de votre locataire à partir de la date de votre choix. Ajoutez chaque réévaluation de loyer pour garder un historique à jour."
          onOpenChange={(nextOpen) => {
            if (!nextOpen) {
              resetForm();
              setError(null);
            }
            onOpenChange(nextOpen);
          }}
        >
          <Form>
            <ModalDialogBody className={styles.body}>
              <FieldArray
                name="paliers"
                render={(arrayHelpers) => (
                  <>
                    {values.paliers.map((palier, index) => (
                      <Card padding="md" key={index}>
                        <div className={styles.periodHead}>
                          {index === 0 ? (
                            <Status
                              color="info"
                              weight="bold"
                              text="Début de l'historique"
                            />
                          ) : (
                            <Status
                              color="violet"
                              weight="bold"
                              text="Augmentation de loyer ou de charge"
                            />
                          )}
                          {index > 0 && (
                            <IconButton
                              type="button"
                              icon={IconTrash}
                              label="Supprimer cette augmentation"
                              color="danger"
                              size="sm"
                              onClick={() => arrayHelpers.remove(index)}
                            />
                          )}
                        </div>
                        <div className={styles.periodGrid}>
                          <TextInput
                            type="date"
                            name={`paliers[${index}].date_palier`}
                            label={
                              index === 0
                                ? "Date de début de l'historique"
                                : "Date d'augmentation"
                            }
                            marginForm=""
                          />
                          <TextInput
                            type="number"
                            name={`paliers[${index}].loyer_hc`}
                            label="Loyer hors charges"
                            placeholder="400"
                            unit="€"
                            step="0.01"
                            marginForm=""
                          />
                          <TextInput
                            type="number"
                            name={`paliers[${index}].charges`}
                            label="Charges"
                            placeholder="40"
                            unit="€"
                            step="0.01"
                            marginForm=""
                          />
                        </div>
                        <div className={styles.periodTotal}>
                          <Text as="span" variant="deci">
                            Loyer total
                          </Text>
                          <Text as="span" weight="bold">
                            {currencyFormatter(palierTotal(palier))}
                          </Text>
                          <Text as="span" variant="deci">
                            / mois
                          </Text>
                        </div>
                      </Card>
                    ))}
                    <Button
                      type="button"
                      variant="secondary"
                      icon={IconPlus}
                      onClick={() => {
                        const last = values.paliers[values.paliers.length - 1];
                        arrayHelpers.push({
                          date_palier: "",
                          loyer_hc: last?.loyer_hc ?? "",
                          charges: last?.charges ?? "",
                        });
                      }}
                    >
                      Ajouter une augmentation de loyer et/ou charge
                    </Button>
                  </>
                )}
              />

              <Checkbox name="loyer_a_jour">
                Ajouter aussi les paiements correspondants (le locataire sera
                marqué à jour)
              </Checkbox>

              <Callout variant="danger">{error}</Callout>
            </ModalDialogBody>
            <ModalDialogFooter
              start={
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    resetForm();
                    setError(null);
                    onOpenChange(false);
                  }}
                >
                  Annuler
                </Button>
              }
            >
              <FormikSubmitButton requiresWrite>Valider</FormikSubmitButton>
            </ModalDialogFooter>
          </Form>
        </ModalDialog>
      )}
    </Formik>
  );
}
